import type { Bereichsgruppenkennung, Bereichskennung } from '@crm/kontrakte';

/**
 * DIE ADRESSEN DER ANWENDUNG (M-01, NFA-15).
 *
 * ============================================================================
 * DER FEHLER, AUS DEM DIESES MODUL ENTSTAND
 * ============================================================================
 *
 * Auftraggeber am 12.09.2026: „der angezeigte link zeigt auf kennzahlen, egal
 * auf welcher seite man sich befindet, das führt dazu, dass man beim reload auf
 * der falschen seite landet."
 *
 * Die Arbeitsfläche führte ihre Stelle ausschließlich im React-Zustand. Die
 * Adresszeile zeigte damit, was den Browser irgendwann einmal geöffnet hatte —
 * und blieb dann stehen, gleich wohin man ging. Was daraus folgt, ist mehr als
 * ein schiefer Link:
 *
 *   NEU LADEN VERLIERT DEN PLATZ. F5 auf der Honorartabelle landete auf dem
 *   Arbeitsplatz. Bei einer Maske, in der man gerade sechsundsiebzig Beträge
 *   tippt, ist das kein Schönheitsfehler.
 *
 *   ZURÜCK VERLÄSST DIE ANWENDUNG. Der Rückwärtspfeil hatte innerhalb des CRM
 *   nichts zu tun — er ging auf die Seite davor, also hinaus.
 *
 *   EIN LINK IST NICHTS WERT. „Schau dir Rechnung 2026-0042 an" ließ sich nicht
 *   schicken, weil jede Adresse dieselbe Seite zeigte.
 *
 * ============================================================================
 * EIN VOLLSTÄNDIGER RECORD, UND DAS IST DER PUNKT
 * ============================================================================
 *
 * `Record<Maske['art'], Weg>` — dieselbe Überlegung wie bei `BEREICH_ZU_MASKE`:
 * Eine Maske ohne Adresse ist ein **Typfehler beim Übersetzen** und nicht eine
 * Seite, die sich nach einem Neuladen in Luft auflöst. Wer eine Maske hinzufügt,
 * wird gefragt, wie sie heißt.
 *
 * ============================================================================
 * WARUM PFADE UND KEINE RAUTENADRESSEN
 * ============================================================================
 *
 * `/rechnungen/abc` und nicht `/#/rechnungen/abc`. Die Kante liefert jeden Pfad
 * als dieselbe Seite aus (SPA-Rückfall im Frontend-Container), der Bau ist also
 * schon da. Eine Rautenadresse wäre der Weg, den man nimmt, wenn man den Server
 * nicht anfassen kann — und sie erscheint in keinem Zugriffsprotokoll, was die
 * Fehlersuche verschlechtert.
 *
 * ============================================================================
 * WAS NICHT IN DIE ADRESSE GEHÖRT
 * ============================================================================
 *
 * Die KENNUNG eines Datensatzes gehört hinein: Sie ist die Identität der Seite,
 * und ohne sie zeigt „zurück" auf eine Akte ohne Akte.
 *
 * Die VORBELEGUNGEN gehören nicht hinein — `suche`, `name`, `bezeichnung`,
 * `kunde`. (Die Buchungsnummer war bis Auftrag #36 eine; seit die Buchung
 * eine Akte mit Reitern ist, ist sie deren Identität und steht drin.) Sie sind der Anlauf eines Sprungs („aus der Suche heraus
 * einen Kunden anlegen, Name schon getippt") und nach dem ersten Tastendruck
 * überholt. In der Adresse wären sie eine Behauptung, die beim Neuladen ein
 * ausgefülltes Formular verspricht, das es nicht mehr gibt.
 *
 * Die MELDUNG schon gar nicht (`kundenakte.meldung`): „Kunde angelegt" nach
 * einem Neuladen noch einmal zu zeigen, wäre eine Nachricht über etwas, das
 * gerade nicht passiert ist.
 *
 * Die ANGEWANDTEN FILTER einer Liste dagegen schon (Auftrag CRM-70, die
 * Buchungsliste): Sie sind keine halb getippte Eingabe, sondern die Ansicht,
 * die gerade zu sehen ist — und „zurück" aus einer Buchung soll genau sie
 * wiederherstellen. Sie stehen im Abfrageteil (`/buchungen?status=gebucht`),
 * nicht als Segment: Sie sind wahlfrei, beliebig kombiniert, und keine
 * Identität.
 */

/**
 * Welche Maske gerade offen ist.
 *
 * Als ausdrueckliche Aufzaehlung, damit es keine Zustaende gibt, die es nicht
 * geben darf — dieselbe Ueberlegung wie in App.tsx.
 *
 * SIE STEHT IN DIESEM MODUL UND NICHT IN DER ARBEITSFLAECHE, seit es Adressen
 * gibt: Die Aufzaehlung IST der Adressraum, und beides nebeneinander waeren zwei
 * Listen, von denen eine hinterherhinkt.
 */
/**
 * Von wo aus die Kundenmasken geöffnet wurden (Auftrag #9, 27.09.2026).
 *
 * Der Vertrieb betreut Firmen und öffentlichen Dienst, der Service die
 * Privatkunden (Tab. 11 i.d.F. AeA-01). Beide arbeiten auf DEMSELBEN Bestand
 * und mit denselben Masken — was sich unterscheidet, sind die Vorbelegung der
 * Liste und der Menüpunkt, der dabei leuchtet. Fehlt der Einstieg, ist es der
 * Vertrieb: So öffnen Suche, Angebot und Auftrag die Akte wie bisher.
 */
export type Einstieg = 'vertrieb' | 'service';

/**
 * Die Reiter der Buchungsakte (Auftrag #36, ÄA-41), in der Folge der Leiste.
 * Sie stehen HIER und nicht in der Maske, weil sie Teil der Adresse sind:
 * `/buchungen/<nummer>/teilnehmer`. Ein Reiter, der hier fehlt, ist in der
 * Adresse ein unbekannter Pfad und kein stiller Rückfall auf die Übersicht.
 */
export const BUCHUNGSREITER = [
  'uebersicht',
  'teilnehmer',
  'zusatzleistungen',
  'storno',
  'beleg',
  'verlauf',
  'bedingungen',
] as const;
export type Buchungsreiter = (typeof BUCHUNGSREITER)[number];

/**
 * Die Filter der Buchungsliste (Auftrag CRM-70), in der Folge des
 * Abfrageteils. Dieselben Namen wie die Felder der Buchungssuche (BUC-80) —
 * die Nummer nicht: Sie öffnet die Buchung direkt.
 */
export const BUCHUNGSFILTER = ['von', 'bis', 'status', 'produkt', 'name'] as const;
export type Buchungsfilter = Readonly<Partial<Record<(typeof BUCHUNGSFILTER)[number], string>>>;

/** Nur die gesetzten Filter, getrimmt und in fester Folge. */
export function gesetzteFilter(filter: Buchungsfilter | undefined): Buchungsfilter {
  const aus: Partial<Record<(typeof BUCHUNGSFILTER)[number], string>> = {};
  for (const schluessel of BUCHUNGSFILTER) {
    const wert = filter?.[schluessel]?.trim() ?? '';
    if (wert !== '') aus[schluessel] = wert;
  }
  return aus;
}

/**
 * Wohin der Menüpunkt „Buchungen" führt (Auftrag CRM-95): aus einer Buchung
 * oder der gefilterten Liste in die Liste mit denselben Filtern, sonst in die
 * ungefilterte. Der Verweis „Alle Buchungen" über der Buchung, der das bisher
 * tat, ist entfallen.
 */
export function buchungslisteVon(jetzt: Maske): Maske {
  /* Auftrag CRM-175: auch aus dem Anlegen — es nahm die Filter der Liste mit. */
  const filter =
    jetzt.art === 'buchungsvorgang' || jetzt.art === 'buchungAnlegen' ? gesetzteFilter(jetzt.filter) : {};
  return Object.keys(filter).length === 0 ? { art: 'buchungsvorgang' } : { art: 'buchungsvorgang', filter };
}

/**
 * Die Ansichten der Termine (Auftrag CRM-67, TAG-29). Der Kalender ist der
 * Anfang und steht nicht in der Adresse — `/termine` bleibt, was es war; die
 * beiden anderen sind das zweite Segment: `/termine/tage`, `/termine/liste`.
 * Sie stehen in der Adresse wie die Reiter der Buchungsakte, damit ein
 * Neuladen in derselben Ansicht bleibt.
 */
export const TERMINANSICHTEN = ['kalender', 'tage', 'liste'] as const;
export type Terminansicht = (typeof TERMINANSICHTEN)[number];

/** Ein Kalendertag als `JJJJ-MM-TT`, den es wirklich gibt — kein 31.02. */
function istTag(wert: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(wert)) return false;
  const d = new Date(`${wert}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === wert;
}

export type Maske =
  | { readonly art: 'arbeitsplatz' }
  | { readonly art: 'kundenliste'; readonly suche?: string; readonly einstieg?: Einstieg }
  | { readonly art: 'kundeAnlegen'; readonly name?: string; readonly einstieg?: Einstieg }
  | {
      readonly art: 'kundenakte';
      readonly id: string;
      readonly meldung?: string;
      readonly einstieg?: Einstieg;
    }
  | { readonly art: 'zusammenfuehren'; readonly ziel: string; readonly einstieg?: Einstieg }
  /* WEB-02: die Anfragen aus den Formularen des Portals. */
  | { readonly art: 'webanfragen' }
  /* WEB-10 i.d.F. AeA-37: die Zuordnung neuer Online-Konten. */
  | { readonly art: 'kontozuordnungen' }
  | { readonly art: 'artikelliste' }
  | { readonly art: 'artikelAnlegen'; readonly bezeichnung?: string }
  | { readonly art: 'artikelakte'; readonly id: string }
  | { readonly art: 'warengruppen' }
  | { readonly art: 'preislisten' }
  | { readonly art: 'rabattkonditionen' }
  | { readonly art: 'angebotsliste' }
  | {
      readonly art: 'angebotAnlegen';
      readonly kunde?: string;
      /** SUP-23 (CRM-178): aus einem Postfachvorgang — nach dem Speichern meldet das Angebot sich dort zurück. Zustand, nicht Adresse. */
      readonly folge?: { readonly vorgang: string; readonly postfach: string; readonly folgeaktion: string };
    }
  | { readonly art: 'angebotsakte'; readonly id: string }
  /* ANG-38: die Arbeitsliste der Disposition. Sie traegt keine Kennung — es
     ist eine LISTE, und was darin steht, entscheidet die Frist. */
  | { readonly art: 'optionsanfragen' }
  | { readonly art: 'auftragsliste' }
  | { readonly art: 'auftragAnlegen'; readonly kunde?: string }
  | { readonly art: 'auftragsakte'; readonly id: string }
  | { readonly art: 'fakturierungsliste' }
  | { readonly art: 'rahmenliste' }
  | { readonly art: 'rahmenAnlegen' }
  | { readonly art: 'rahmenakte'; readonly id: string }
  | { readonly art: 'belegvorlagen' }
  | { readonly art: 'postfaecher' }
  | { readonly art: 'portalzugaenge' }
  /* TAG-49: die Check-in-Tablets. */
  | { readonly art: 'checkingeraete' }
  | { readonly art: 'navision' }
  /* AUS-15 (Auftrag #30): alle Abos des Berichtsversands, fuer die Administration. */
  | { readonly art: 'berichtsversand' }
  | { readonly art: 'erechnung' }
  /* KI-05 bis KI-08, KI-21 (Auftrag #53): der Modellzugang unter Umsysteme. */
  | { readonly art: 'ki' }
  | { readonly art: 'geschaeftsdaten' }
  | { readonly art: 'meinkonto' }
  | { readonly art: 'systemzustand' }
  /*
   * Die Buchungsakte (Auftrag #36): Die Nummer ist ihre Identität und steht
   * in der Adresse, der Reiter als drittes Segment. Ohne Nummer ist es die
   * Suche nach einer Buchung.
   */
  | {
      readonly art: 'buchungsvorgang';
      readonly nummer?: string;
      readonly reiter?: Buchungsreiter;
      /*
       * Auftrag CRM-70: die Filter der Liste. Ohne Nummer stehen sie in der
       * Adresse; mit Nummer reisen sie nur mit, damit der Menüpunkt
       * „Buchungen" (CRM-95) die Liste so wiederherstellt, wie sie war.
       */
      readonly filter?: Buchungsfilter;
      /** Eine Rückmeldung beim Öffnen („angelegt", CRM-175) — Zustand, nicht Adresse. */
      readonly meldung?: string;
    }
  /*
   * BUC-50/BUC-51 (Auftrag CRM-175): eine Buchung anlegen, als eigene Seite
   * ohne Suchreihe und Liste. Die Filter der Liste reisen nur mit — Abbrechen,
   * „zurück" und der Menüpunkt „Buchungen" stellen sie wieder her.
   */
  | { readonly art: 'buchungAnlegen'; readonly filter?: Buchungsfilter }
  | { readonly art: 'arbeitsliste' }
  /* BUC-129 bis BUC-132: die Faelle abgesagter Termine. */
  | { readonly art: 'absagen' }
  /* LEG-17, Paket 8 G: der Widerruf im Haus. */
  | { readonly art: 'widerrufe' }
  | { readonly art: 'empfang' }
  /* SUP-53 (AeA-51): die Google-Rezensionen. */
  | { readonly art: 'rezensionen' }
  /*
   * Auftrag CRM-188: Der geöffnete Vorgang steht in der Adresse
   * (`/postfaecher/<id>`), eine aussortierte Nachricht unter
   * `/postfaecher/aussortiert/<id>` — die Liste ist dieselbe Seite. `postfach`
   * ist der Reiter, den ein Sprung (SUP-11, Glocke) vorbelegt: Vorbelegung,
   * nicht Adresse.
   */
  | {
      readonly art: 'posteingang';
      readonly vorgang?: string;
      readonly nachricht?: string;
      readonly postfach?: string;
    }
  /* KUN-15: „Meine Wiedervorlagen". `oeffnen`: die Wiedervorlage aus der Glocke (Auftrag CRM-170), Vorbelegung und nicht Adresse. */
  | { readonly art: 'wiedervorlagen'; readonly oeffnen?: string }
  | { readonly art: 'assets' }
  | { readonly art: 'trainerstamm' }
  | { readonly art: 'besetzung' }
  | { readonly art: 'bausteine' }
  | { readonly art: 'honorare' }
  /*
   * TAG-29 (Auftrag CRM-67): der Sprung aus den Terminen in den Tagesplan.
   * Der Tag ist die Identität des Tagesplans und steht als zweites Segment,
   * der Standort als drittes — nur mit Tag. Ohne beides ist es der Einstieg
   * über das Menü, und die Planung öffnet heute am Hauptstandort.
   */
  | { readonly art: 'trainingsplanung'; readonly tag?: string; readonly standort?: string }
  | { readonly art: 'gebuehrenstaffeln' }
  | { readonly art: 'rechtstexte' }
  /* SUP-20 (Auftrag CRM-178): die Textbausteine der Postfächer. */
  | { readonly art: 'textbausteine' }
  | { readonly art: 'datenschutz' }
  | { readonly art: 'einstellungen' }
  /* RES-01, TAG-01: die Standorte selbst (Auftrag #12). */
  | { readonly art: 'standorte' }
  /* FRS-08, FRS-09: Feiertage und Schliesstage je Standort. */
  | { readonly art: 'kalender' }
  | { readonly art: 'uebernahme' }
  | { readonly art: 'termine'; readonly ansicht?: Terminansicht }
  /* CRM-117 (L6): die Akte eines Termins — Kopf, Teilnehmende, Check-in. */
  | { readonly art: 'terminakte'; readonly id: string }
  | { readonly art: 'benutzerliste' }
  | { readonly art: 'benutzerakte'; readonly id: string }
  | { readonly art: 'bestaetigungen' }
  | { readonly art: 'rechnungsliste' }
  /* BUC-75, M-61 (CRM-152): Auftrag anlegen und gleich abrechnen. */
  | { readonly art: 'rechnungAnlegen'; readonly kunde?: string }
  | { readonly art: 'rechnungsakte'; readonly id: string }
  | { readonly art: 'offeneposten' }
  /* PRD-38, PRF-17 (CRM-157): Sammelabrechnung der Gutscheine je Kostenträger. */
  | { readonly art: 'bgAbrechnung' }
  | { readonly art: 'betriebspruefung' }
  /* AUS-16 (Auftrag #24): die eigenen Berichte, der Baukasten und ein gespeicherter Bericht. */
  | { readonly art: 'berichtsliste' }
  | { readonly art: 'berichtNeu' }
  | { readonly art: 'berichtsakte'; readonly id: string }
  /**
   * Ein Bereich, dessen Maske noch nicht gebaut ist.
   *
   * `kommt` ist der Unterschied zwischen einer Sackgasse und einer Ankuendigung
   * (B3, 12.09.2026): Bis hierher stand dort nur "noch nicht gebaut", und das
   * traf ausgerechnet die Geschaeftsleitung, deren Dashboard-Kachel
   * "Deckungsbeitrag" genau dorthin springt.
   */
  | {
      readonly art: 'unfertig';
      readonly bereich: string;
      readonly beschriftung: string;
      readonly kommt?: string;
    }
  /**
   * Die Seite einer Menuegruppe (12.09.2026).
   *
   * Sie ist eine Maske wie jede andere und kein Sonderzustand daneben: Der Kopf
   * traegt ihren Titel, die Ueberschrift bekommt den Fokus, und der Weg dorthin
   * ist derselbe Wechsel. Ein zweiter Zustand „Gruppenseite offen" neben `maske`
   * waere die Stelle, an der beide auseinanderlaufen.
   */
  | { readonly art: 'bereichsseite'; readonly gruppe: Bereichsgruppenkennung };

/** Die Adresse einer Maske und der Menuepunkt, der dabei leuchtet. */
type Weg = {
  /**
   * Das erste Segment des Pfades, ohne Schrägstrich.
   *
   * Bei einer Maske, die zu einem Menüpunkt gehört, ist es dessen Kennung — dann
   * ist die Adresse `/honorare` und nicht `/stammdaten/honorare`: Die
   * Bereichskennungen sind schon eindeutig (`BEREICHSKENNUNGEN`), und ein
   * Gruppensegment davor wäre ein Pfad, der sich beim nächsten Umbau des Menüs
   * ändert, obwohl die Seite dieselbe ist.
   */
  readonly pfad: string;
  /**
   * Welcher MENÜPUNKT dabei als aktuell gilt.
   *
   * `null` heißt: keiner — die Maske hängt an keinem Punkt („Mein Konto"), und
   * die Hervorhebung bleibt, wo sie war. Ohne dieses `null` müsste jede solche
   * Maske sich einen Punkt ausleihen, und der leuchtete dann falsch.
   */
  readonly bereich: Bereichskennung | null;
  /**
   * Welches Feld der Maske als zweites Segment steht.
   *
   * Die Identität der Seite, nicht ihre Vorbelegung — die Begründung steht im
   * Kopf dieser Datei.
   *
   * `nummer` (Auftrag #36): die Buchungsnummer der Buchungsakte. Anders als
   * `id` ist sie WAHLFREI — `/buchungen` ohne Nummer ist dieselbe Maske, mit
   * dem Suchfeld.
   */
  readonly feld?: 'id' | 'ziel' | 'nummer';
  /**
   * Die Reiter, die als DRITTES Segment stehen dürfen (Auftrag #36) — nur
   * hinter dem Feld und nur diese. Alles andere bleibt bei höchstens zwei
   * Segmenten.
   */
  readonly reiter?: readonly string[];
};

/**
 * Die Adresse jeder Maske.
 *
 * NACH BEREICH GEORDNET und nicht alphabetisch: Wer eine Maske sucht, sucht sie
 * bei ihren Nachbarn. Die Reihenfolge ist dieselbe wie in `BEREICH_ZU_MASKE`.
 */
export const WEGE: Record<Maske['art'], Weg> = {
  arbeitsplatz: { pfad: 'dashboard', bereich: 'dashboard' },
  /* KUN-15 */
  wiedervorlagen: { pfad: 'wiedervorlagen', bereich: 'wiedervorlagen' },

  /* Kunden (KUN) */
  kundenliste: { pfad: 'kunden', bereich: 'kunden' },
  kundeAnlegen: { pfad: 'kunden-neu', bereich: 'kunden' },
  kundenakte: { pfad: 'kunden', bereich: 'kunden', feld: 'id' },
  zusammenfuehren: { pfad: 'kunden-zusammenfuehren', bereich: 'kunden', feld: 'ziel' },
  webanfragen: { pfad: 'webanfragen', bereich: 'kunden' },
  kontozuordnungen: { pfad: 'kontozuordnungen', bereich: 'kunden' },

  /* Produkte (PRD) — drei Sichten auf denselben Bestand, ein Menüpunkt */
  artikelliste: { pfad: 'produkte', bereich: 'produkte' },
  artikelAnlegen: { pfad: 'produkte-neu', bereich: 'produkte' },
  artikelakte: { pfad: 'produkte', bereich: 'produkte', feld: 'id' },
  warengruppen: { pfad: 'warengruppen', bereich: 'produkte' },
  preislisten: { pfad: 'preislisten', bereich: 'produkte' },
  rabattkonditionen: { pfad: 'rabattkonditionen', bereich: 'produkte' },
  bausteine: { pfad: 'bausteine', bereich: 'bausteine' },
  honorare: { pfad: 'honorare', bereich: 'honorare' },

  /* Angebote (ANG) */
  angebotsliste: { pfad: 'angebote', bereich: 'angebote' },
  angebotAnlegen: { pfad: 'angebote-neu', bereich: 'angebote' },
  angebotsakte: { pfad: 'angebote', bereich: 'angebote', feld: 'id' },
  optionsanfragen: { pfad: 'anfragen', bereich: 'anfragen' },

  /* Aufträge (AUF) */
  auftragsliste: { pfad: 'auftraege', bereich: 'auftraege' },
  auftragAnlegen: { pfad: 'auftraege-neu', bereich: 'auftraege' },
  auftragsakte: { pfad: 'auftraege', bereich: 'auftraege', feld: 'id' },
  fakturierungsliste: { pfad: 'fakturierung', bereich: 'auftraege' },
  rahmenliste: { pfad: 'rahmenauftraege', bereich: 'auftraege' },
  rahmenAnlegen: { pfad: 'rahmenauftraege-neu', bereich: 'auftraege' },
  rahmenakte: { pfad: 'rahmenauftraege', bereich: 'auftraege', feld: 'id' },

  /* Rechnungen (RNG) */
  rechnungsliste: { pfad: 'rechnungen', bereich: 'rechnungen' },
  rechnungAnlegen: { pfad: 'rechnungen-neu', bereich: 'rechnungen' },
  rechnungsakte: { pfad: 'rechnungen', bereich: 'rechnungen', feld: 'id' },
  offeneposten: { pfad: 'offene-posten', bereich: 'rechnungen' },
  bgAbrechnung: { pfad: 'bg-abrechnung', bereich: 'rechnungen' },

  /* Betrieb (TAG, BUC) */
  termine: { pfad: 'termine', bereich: 'termine' },
  terminakte: { pfad: 'termine', bereich: 'termine', feld: 'id' },
  trainingsplanung: { pfad: 'trainingsplanung', bereich: 'trainingsplanung' },
  buchungsvorgang: { pfad: 'buchungen', bereich: 'buchungen', feld: 'nummer', reiter: BUCHUNGSREITER },
  buchungAnlegen: { pfad: 'buchungen-neu', bereich: 'buchungen' },
  arbeitsliste: { pfad: 'arbeitsliste', bereich: 'arbeitsliste' },
  absagen: { pfad: 'absagen', bereich: 'absagen' },
  widerrufe: { pfad: 'widerrufe', bereich: 'widerrufe' },
  empfang: { pfad: 'empfang', bereich: 'empfang' },
  rezensionen: { pfad: 'rezensionen', bereich: 'rezensionen' },
  /* Auftrag CRM-176: die Vorgaenge; die Einrichtung der Postfaecher ist `postfaecher` unter Umsysteme. */
  posteingang: { pfad: 'postfaecher', bereich: 'posteingang' },
  bestaetigungen: { pfad: 'bestaetigungen', bereich: 'termine' },

  /* Ressourcen (RES) */
  assets: { pfad: 'assets', bereich: 'assets' },
  trainerstamm: { pfad: 'trainer', bereich: 'trainer' },
  besetzung: { pfad: 'besetzung', bereich: 'besetzung' },

  /* Berichte (AUS-16) — eine eigene Gruppe der Menüzeile (Auftrag #24) */
  berichtsliste: { pfad: 'berichte', bereich: 'berichte' },
  berichtNeu: { pfad: 'berichte-neu', bereich: 'berichte' },
  berichtsakte: { pfad: 'berichte', bereich: 'berichte', feld: 'id' },

  /* Verwaltung, Belege, Umsysteme, Benutzer und Recht */
  geschaeftsdaten: { pfad: 'verwaltung', bereich: 'verwaltung' },
  einstellungen: { pfad: 'einstellungen', bereich: 'verwaltung' },
  standorte: { pfad: 'standorte', bereich: 'verwaltung' },
  kalender: { pfad: 'kalender', bereich: 'verwaltung' },
  uebernahme: { pfad: 'datenuebernahme', bereich: 'verwaltung' },
  belegvorlagen: { pfad: 'belegvorlagen', bereich: 'belege' },
  gebuehrenstaffeln: { pfad: 'gebuehrenstaffeln', bereich: 'belege' },
  rechtstexte: { pfad: 'rechtstexte', bereich: 'belege' },
  textbausteine: { pfad: 'textbausteine', bereich: 'belege' },
  postfaecher: { pfad: 'umsysteme', bereich: 'umsysteme' },
  portalzugaenge: { pfad: 'portalzugaenge', bereich: 'umsysteme' },
  navision: { pfad: 'navision', bereich: 'umsysteme' },
  berichtsversand: { pfad: 'berichtsversand', bereich: 'belege' },
  erechnung: { pfad: 'erechnung', bereich: 'umsysteme' },
  ki: { pfad: 'ki', bereich: 'umsysteme' },
  benutzerliste: { pfad: 'benutzer', bereich: 'benutzer' },
  benutzerakte: { pfad: 'benutzer', bereich: 'benutzer', feld: 'id' },
  /* TAG-49: seit Auftrag #56 bei den Benutzern statt unter den Umsystemen — die Adresse blieb. */
  checkingeraete: { pfad: 'checkin-tablets', bereich: 'benutzer' },
  datenschutz: { pfad: 'datenschutz', bereich: 'datenschutz' },
  betriebspruefung: { pfad: 'betriebspruefung', bereich: 'betriebspruefung' },
  systemzustand: { pfad: 'systemzustand', bereich: 'systemzustand' },

  /*
   * „Mein Konto" hängt an keinem Menüpunkt — es kommt aus dem Benutzermenü. Der
   * Pfad gibt es trotzdem, damit ein Neuladen dort bleibt.
   */
  meinkonto: { pfad: 'mein-konto', bereich: null },

  /*
   * Diese zwei tragen ihre Adresse SELBST: Der Pfad einer ungebauten Maske ist
   * ihr Bereich, der einer Gruppenseite ihre Gruppe. Ein fester Wert stünde hier
   * für zwei verschiedene Seiten — `unfertig` steht jedem Bereich offen, der
   * noch keine Maske hat, und `bereichsseite` gibt es neunmal.
   */
  unfertig: { pfad: '', bereich: null },
  bereichsseite: { pfad: '', bereich: null },
};

/** Die Masken, die einen Einstieg tragen können. */
type MitEinstieg = Extract<Maske, { readonly einstieg?: Einstieg }>['art'];

/**
 * DIE KUNDENMASKEN AUS DEM SERVICE (Auftrag #9, 27.09.2026).
 *
 * Eigener Pfad und eigener Menüpunkt, dieselbe Maske. Der Einstieg steht in der
 * ADRESSE und nicht nur im Zustand: Nach einem Neuladen auf der Akte, die man
 * aus dem Service geöffnet hat, leuchtet weiter „Service → Kunden", und die
 * Liste dahinter ist wieder auf Privatkunden gestellt.
 *
 * `feld` übernimmt jeder Eintrag von seinem Gegenstück in `WEGE` — die Akte
 * ist dort wie hier `…/<id>`.
 */
const IM_SERVICE: Readonly<Record<MitEinstieg, { readonly pfad: string }>> = {
  kundenliste: { pfad: 'privatkunden' },
  kundeAnlegen: { pfad: 'privatkunden-neu' },
  kundenakte: { pfad: 'privatkunden' },
  zusammenfuehren: { pfad: 'privatkunden-zusammenfuehren' },
};

/** Der Menüpunkt der Kundenmasken im Service. */
const SERVICEBEREICH: Bereichskennung = 'privatkunden';

function imService(maske: Maske): maske is Extract<Maske, { readonly art: MitEinstieg }> {
  return 'einstieg' in maske && maske.einstieg === 'service' && maske.art in IM_SERVICE;
}

/** Auftrag CRM-188: das Segment vor der Kennung einer aussortierten Nachricht. */
const AUSSORTIERT_SEGMENT = 'aussortiert';

/** Der Pfad einer Maske, mit führendem Schrägstrich. */
export function pfadVon(maske: Maske): string {
  if (maske.art === 'unfertig') return `/${maske.bereich}`;
  if (maske.art === 'bereichsseite') return `/${maske.gruppe}`;

  const weg = imService(maske)
    ? { ...WEGE[maske.art], pfad: IM_SERVICE[maske.art].pfad }
    : WEGE[maske.art];
  if (weg.feld === 'id' && 'id' in maske) return `/${weg.pfad}/${maske.id}`;
  if (weg.feld === 'ziel' && 'ziel' in maske) return `/${weg.pfad}/${maske.ziel}`;
  /* Auftrag #36: die Buchungsakte — die Übersicht ist der Anfang und steht nicht in der Adresse. */
  if (maske.art === 'buchungsvorgang' && maske.nummer !== undefined && maske.nummer !== '') {
    const akte = `/${weg.pfad}/${encodeURIComponent(maske.nummer)}`;
    return maske.reiter === undefined || maske.reiter === 'uebersicht' ? akte : `${akte}/${maske.reiter}`;
  }
  /* Auftrag CRM-70: die Filter der Buchungsliste im Abfrageteil. */
  if (maske.art === 'buchungsvorgang') {
    const abfrage = new URLSearchParams(gesetzteFilter(maske.filter)).toString();
    if (abfrage !== '') return `/${weg.pfad}?${abfrage}`;
  }
  /* Auftrag CRM-67: Der Kalender ist der Anfang und steht nicht in der Adresse. */
  if (maske.art === 'termine' && maske.ansicht !== undefined && maske.ansicht !== 'kalender') {
    return `/${weg.pfad}/${maske.ansicht}`;
  }
  /* Auftrag CRM-188: der Vorgang bzw. die aussortierte Nachricht im ganzen Arbeitsbereich. */
  if (maske.art === 'posteingang' && maske.vorgang !== undefined) return `/${weg.pfad}/${maske.vorgang}`;
  if (maske.art === 'posteingang' && maske.nachricht !== undefined) {
    return `/${weg.pfad}/${AUSSORTIERT_SEGMENT}/${maske.nachricht}`;
  }
  if (maske.art === 'trainingsplanung' && maske.tag !== undefined && maske.tag !== '') {
    const tag = `/${weg.pfad}/${maske.tag}`;
    return maske.standort === undefined || maske.standort === ''
      ? tag
      : `${tag}/${encodeURIComponent(maske.standort)}`;
  }
  return `/${weg.pfad}`;
}

/**
 * DIE SEGMENTE NACH DEM PFAD DER TERMINE UND DER PLANUNG (Auftrag CRM-67).
 *
 * Beide Masken tragen keine Kennung, sondern eine Ansicht bzw. einen Tag —
 * eigene Regeln, die vor der allgemeinen Lesart der Akten greifen. Gibt
 * `undefined`, wenn der Pfad keine der beiden ist; `null`, wenn er es ist,
 * aber das Segment nicht stimmt (`/termine/morgen`, `/trainingsplanung/31.02.`).
 */
/** Eine Terminkennung ist eine UUID — `/termine/morgen` bleibt ein Tippfehler. */
const TERMINKENNUNG = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;


function mitSegmenten(teile: readonly string[]): Maske | null | undefined {
  const [erstes, zweites, drittes, viertes] = teile;
  if (zweites === undefined) return undefined;
  if (erstes === WEGE.termine.pfad) {
    if (drittes !== undefined) return null;
    if ((TERMINANSICHTEN as readonly string[]).includes(zweites)) {
      return { art: 'termine', ansicht: zweites as Terminansicht };
    }
    /* CRM-117: eine Kennung statt einer Ansicht ist die Terminakte. */
    return TERMINKENNUNG.test(zweites) ? { art: 'terminakte', id: zweites.toLowerCase() } : null;
  }
  /*
   * Auftrag CRM-188: Vorgang und aussortierte Nachricht tragen eine UUID wie
   * die Termine — `/postfaecher/irgendwas` bleibt ein Tippfehler.
   */
  if (erstes === WEGE.posteingang.pfad) {
    if (zweites === AUSSORTIERT_SEGMENT) {
      return drittes !== undefined && viertes === undefined && TERMINKENNUNG.test(drittes)
        ? { art: 'posteingang', nachricht: drittes.toLowerCase() }
        : null;
    }
    return drittes === undefined && TERMINKENNUNG.test(zweites)
      ? { art: 'posteingang', vorgang: zweites.toLowerCase() }
      : null;
  }
  if (erstes === WEGE.trainingsplanung.pfad) {
    if (!istTag(zweites) || viertes !== undefined) return null;
    if (drittes === undefined) return { art: 'trainingsplanung', tag: zweites };
    try {
      return { art: 'trainingsplanung', tag: zweites, standort: decodeURIComponent(drittes) };
    } catch {
      return null;
    }
  }
  return undefined;
}

/**
 * Welche SEITE eine Maske ist — ihr Pfad ohne den Reiter (Auftrag #36).
 *
 * Die Arbeitsfläche baut eine Maske neu auf und setzt ihren Kopf zurück, wenn
 * sich die Seite ändert. Ein Reiterwechsel ändert die Adresse, aber nicht die
 * Seite: Die Buchung bleibt geladen, der Kopf bleibt, und der Fokus bleibt
 * auf dem Reiter, statt zur Überschrift zu springen (WCAG 2.4.3).
 */
export function schluesselVon(maske: Maske): string {
  /* Auftrag CRM-70: Ein Filter ändert die Liste, nicht die Seite — der Fokus bleibt im Filter. */
  if (maske.art === 'buchungsvorgang') {
    const { reiter: _reiter, filter: _filter, ...ohne } = maske;
    return pfadVon(ohne);
  }
  /* Auftrag CRM-67: Die Ansicht der Termine wechselt wie ein Reiter — Filter und Monat bleiben. */
  if (maske.art === 'termine') return pfadVon({ art: 'termine' });
  /*
   * Auftrag CRM-68: Ein Tag öffnen, schließen oder blättern bleibt dieselbe
   * Seite — Standort, Zeitraum und Ansicht bleiben, und „zurück" führt in die
   * Liste, ohne die Maske neu aufzubauen.
   */
  if (maske.art === 'trainingsplanung') return pfadVon({ art: 'trainingsplanung' });
  /*
   * Auftrag CRM-188: Ein Vorgang öffnen, schließen oder blättern bleibt
   * dieselbe Seite — Reiter, Filter, Suche und Rolllage der Liste bleiben.
   */
  if (maske.art === 'posteingang') return pfadVon({ art: 'posteingang' });
  return pfadVon(maske);
}

/**
 * Umgekehrt: welche Maske hinter einem Pfad liegt.
 *
 * ============================================================================
 * DIE UMKEHRUNG WIRD GEBILDET UND NICHT GEPFLEGT
 * ============================================================================
 *
 * Aus `WEGE` beim Laden des Moduls, nicht als zweite Tabelle daneben. Zwei
 * handgeschriebene Richtungen wären zwei, die auseinanderlaufen — und zwar
 * genau dann, wenn jemand einen Pfad umbenennt und die eine Seite vergisst.
 *
 * `gefunden` liefert nur die ART und die Kennung. Eine `unfertig`-Maske trägt
 * Beschriftung und Ankündigung, die hier nicht stehen; sie kommt deshalb aus
 * `BEREICH_ZU_MASKE` — der Aufrufer gibt es mit.
 */
/** Was ein Pfad ergibt: die Art und, bei den Kundenmasken, den Einstieg. */
type Ziel = { readonly art: Maske['art']; readonly einstieg?: Einstieg };

/** Alle Wege samt denen aus dem Service, einmal aufgezählt. */
const ALLE_WEGE: readonly (readonly [Ziel, Weg])[] = [
  ...(Object.entries(WEGE) as readonly [Maske['art'], Weg][]).map(
    ([art, weg]) => [{ art }, weg] as const,
  ),
  ...(Object.entries(IM_SERVICE) as readonly [MitEinstieg, { readonly pfad: string }][]).map(
    ([art, { pfad }]) => [{ art, einstieg: 'service' as const }, { ...WEGE[art], pfad }] as const,
  ),
];

const PFAD_ZU_ART = new Map<string, Ziel>(
  ALLE_WEGE
    /* Die zwei ohne festen Pfad tragen ihre Adresse selbst — siehe oben. */
    .filter(([, weg]) => weg.pfad !== '')
    /*
     * ZWEI ARTEN TEILEN EINEN PFAD: `kunden` ist die Liste, `kunden/<id>` die
     * Akte. Die Liste gewinnt hier, und die Akte entsteht unten aus dem zweiten
     * Segment. Deshalb kommen die Akten ZULETZT und überschreiben nichts:
     * `feld` markiert sie, und sie werden übersprungen.
     */
    /* `nummer` ist wahlfrei (Auftrag #36): `/buchungen` allein ist dieselbe Maske. */
    .filter(([, weg]) => weg.feld === undefined || weg.feld === 'nummer')
    .map(([ziel, weg]) => [weg.pfad, ziel]),
);

/** Dieselbe Bildung für die Akten — Pfad → Art, aber nur die mit Kennung. */
const PFAD_ZU_AKTE = new Map<string, Ziel>(
  ALLE_WEGE
    .filter(([, weg]) => weg.pfad !== '' && weg.feld !== undefined)
    .map(([ziel, weg]) => [weg.pfad, ziel]),
);

/**
 * Die Maske zu einem Pfad — oder `null`, wenn der Pfad keine ist.
 *
 * `bereichZuMaske` kommt von außen, weil eine ungebaute Maske ihre Ankündigung
 * dort trägt und nicht hier. Dasselbe gilt für die Gruppenseite: Welche Gruppen
 * es gibt, weiß `@crm/kontrakte`, und der Aufrufer prüft es.
 *
 * EIN UNBEKANNTER PFAD IST KEIN FEHLER, sondern ein Lesezeichen von vorgestern
 * oder ein Tippfehler. Er gibt `null`, und der Aufrufer landet auf dem
 * Arbeitsplatz — mit einer berichtigten Adresse, damit die Zeile nicht weiter
 * etwas behauptet, was nicht stimmt.
 */
/**
 * ADRESSEN, DIE ES NICHT MEHR GIBT, UND WOHIN SIE JETZT FÜHREN.
 *
 * Ein Lesezeichen auf den alten Platzhalter „Auswertungen" unter System
 * landet auf den Berichten, die an seine Stelle getreten sind (Auftrag #24) —
 * nicht auf dem Arbeitsplatz. Die Arbeitsfläche berichtigt danach die
 * Adresszeile (`replaceState`), sodass das nächste Lesezeichen stimmt.
 */
const ALTE_PFADE: Readonly<Record<string, string>> = {
  auswertungen: 'berichte',
};

export function maskeAus(
  pfad: string,
  bereichZuMaske: Readonly<Record<Bereichskennung, Maske>>,
  gruppen: readonly Bereichsgruppenkennung[],
): Maske | null {
  /* Auftrag CRM-70: Den Abfrageteil liest nur die Buchungsliste; alle anderen übergehen ihn. */
  const frage = pfad.indexOf('?');
  const abfrage = frage < 0 ? '' : pfad.slice(frage + 1);
  const teile = (frage < 0 ? pfad : pfad.slice(0, frage)).split('/').filter((t) => t !== '');
  const [alt, zweites, drittes] = teile;
  const erstes = alt === undefined ? undefined : (ALTE_PFADE[alt] ?? alt);
  if (erstes === undefined) return null;
  const besondere = mitSegmenten([erstes, ...teile.slice(1)]);
  if (besondere !== undefined) return besondere;
  /*
   * HÖCHSTENS ZWEI SEGMENTE. `/kunden/abc/mehr` ist keine Adresse, die diese
   * Anwendung erzeugt — sie stillschweigend als Kundenakte zu lesen hieße, die
   * Zeile etwas anderes zeigen zu lassen als den Bildschirm. Genau das war der
   * Fehler, um dessen Behebung es hier geht.
   */
  if (teile.length > 3) return null;
  if (teile.length === 3) {
    /* Das dritte Segment nur als bekannter Reiter einer Akte (Auftrag #36). */
    const akte = alt === undefined ? undefined : PFAD_ZU_AKTE.get(alt);
    const reiter = akte === undefined ? undefined : WEGE[akte.art].reiter;
    if (reiter === undefined || drittes === undefined || !reiter.includes(drittes)) return null;
  }

  /* Eine Gruppenseite: der Pfad IST die Gruppe. */
  if (zweites === undefined && (gruppen as readonly string[]).includes(erstes)) {
    return { art: 'bereichsseite', gruppe: erstes as Bereichsgruppenkennung };
  }

  if (zweites !== undefined) {
    const akte = PFAD_ZU_AKTE.get(erstes);
    if (akte === undefined) return null;
    const feld = WEGE[akte.art].feld;
    if (feld === 'nummer') {
      let nummer: string;
      try {
        nummer = decodeURIComponent(zweites);
      } catch {
        /* Ein kaputtes %-Zeichen ist ein Tippfehler in der Zeile, keine Buchung. */
        return null;
      }
      return {
        ...akte,
        nummer,
        ...(drittes === undefined ? {} : { reiter: drittes }),
      } as Maske;
    }
    return feld === 'ziel'
      ? ({ ...akte, ziel: zweites } as Maske)
      : ({ ...akte, id: zweites } as Maske);
  }

  const ziel = PFAD_ZU_ART.get(erstes);
  if (ziel === undefined) {
    /*
     * Kein eigener Weg — aber vielleicht ein BEREICH ohne eigene Maske, also
     * eine ungebaute. `BEREICH_ZU_MASKE` trägt sie samt Ankündigung.
     */
    const ausBereich = (bereichZuMaske as Record<string, Maske | undefined>)[erstes];
    return ausBereich ?? null;
  }
  if (ziel.art === 'buchungsvorgang') {
    const filter = gesetzteFilter(Object.fromEntries(new URLSearchParams(abfrage)));
    return Object.keys(filter).length === 0 ? { art: 'buchungsvorgang' } : { art: 'buchungsvorgang', filter };
  }
  return { ...ziel } as Maske;
}

/**
 * Welcher Menuepunkt leuchten soll, wenn diese Maske offen ist.
 *
 * `bereich: null` heisst „nicht anfassen" und nicht „nichts hervorheben": „Mein
 * Konto" kommt aus dem Benutzermenü, und wer von den Honoraren dorthin geht, soll
 * nach dem Zurück nicht raten muessen, wo er war.
 */
export function bereichVon(maske: Maske): Bereichskennung | null {
  if (maske.art === 'unfertig') return maske.bereich as Bereichskennung;
  if (maske.art === 'bereichsseite') return null;
  /* Die Markierung folgt dem Einstieg, auch in der Akte (Auftrag #9). */
  if (imService(maske)) return SERVICEBEREICH;
  return WEGE[maske.art].bereich;
}
